// src/components/User/QuickUserActions.tsx
import React from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, Badge } from "@/components/common";
import { PlayCircle, Eye, Package, Brain, CheckCircle } from "lucide-react";

interface Props {
  /** Objeto de progreso (puede venir de useHierarchicalProgress) */
  progress?: any;
  /** Título opcional del bloque */
  title?: string;
}

/**
 * QuickUserActions
 * Accesos rápidos entre módulos dentro de una categoría
 */
const QuickUserActions: React.FC<Props> = ({
  progress,
  title = "Explora los módulos del curso",
}) => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const location = useLocation();

  const actions = [
    {
      id: "video",
      title: "Videos",
      description: "Aprende con material audiovisual educativo",
      icon: PlayCircle,
      color: "text-brinsa-rojo",
      bg: "bg-brinsa-rojo/10",
      path: `/category/${categoryId}/video`,
      completed: progress?.video ?? false,
    },
    {
      id: "ar",
      title: "Realidad Aumentada",
      description: "Explora modelos 3D interactivos",
      icon: Eye,
      color: "text-brinsa-morado",
      bg: "bg-brinsa-morado/10",
      path: `/category/${categoryId}/ar`,
      completed: progress?.ar ?? false,
    },
    {
      id: "portfolio",
      title: "Portafolio de Productos",
      description: "Consulta las presentaciones disponibles",
      icon: Package,
      color: "text-brinsa-celeste",
      bg: "bg-brinsa-celeste/10",
      path: `/category/${categoryId}/portfolio`,
      completed: progress?.portfolio ?? false,
    },
    {
      id: "test",
      title: "Prueba de Conocimiento",
      description: "Evalúa lo aprendido",
      icon: Brain,
      color: "text-brinsa-verde",
      bg: "bg-brinsa-verde/10",
      path: `/category/${categoryId}/test`,
      completed: progress?.test ?? false,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <Card title={title}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {actions.map((action) => {
            const isActive = location.pathname.includes(action.path);
            return (
              <Link key={action.id} to={action.path} className="group">
                <motion.div
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.2 }}
                  className={`p-4 rounded-lg border-2 transition-all relative ${
                    isActive
                      ? "border-brinsa-azul bg-brinsa-azul/5 shadow-sm"
                      : "border-slate-200 hover:border-slate-300 hover:shadow-md"
                  }`}
                >
                  <div
                    className={`w-10 h-10 ${action.bg} rounded-xl grid place-items-center mb-3`}
                  >
                    <action.icon className={`w-5 h-5 ${action.color}`} />
                  </div>

                  <h3 className="font-medium text-brinsa-azul mb-1">
                    {action.title}
                  </h3>
                  <p className="text-sm text-brinsa-azulOscuro/80">
                    {action.description}
                  </p>

                  {action.completed && (
                    <div className="absolute top-3 right-3">
                      <Badge variant="success" size="sm">
                        <CheckCircle className="w-3 h-3 mr-1" /> Listo
                      </Badge>
                    </div>
                  )}
                </motion.div>
              </Link>
            );
          })}
        </div>
      </Card>
    </motion.div>
  );
};

export default QuickUserActions;
